import { useState, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import './Monitoring.css';
import Menu from '../Shared/Menu';
import usePageTitle from '../Shared/usePageTitle';
import {
    AUTOMATION_HEALTH_ROWS,
    AUTOMATION_STATUS_COLORS,
    AUTOMATION_BADGE_CLASS,
} from '../../data/monitoringData';

const FILTERS = ['All', 'Running', 'Success', 'Error', 'Never Run', 'Stale', 'Paused', 'Stopped'];

const KPIS = [
    {
        filter: 'Error', cardClass: 'ah-kpi-error', iconClass: 'ah-icon-error', valueClass: 'ah-kpi-value-error',
        label: 'Currently Erroring', subtext: 'last run ended in error',
        icon: <><circle cx="12" cy="12" r="10" /><path d="M15 9l-6 6M9 9l6 6" /></>,
    },
    {
        filter: 'Never Run', cardClass: 'ah-kpi-never', iconClass: 'ah-icon-never', valueClass: 'ah-kpi-value-never',
        label: 'Never Run', subtext: 'never been triggered',
        icon: <><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></>,
    },
    {
        filter: 'Stale', cardClass: 'ah-kpi-stale', iconClass: 'ah-icon-stale', valueClass: 'ah-kpi-value-stale',
        label: 'Stale', subtext: 'not run in 30 days',
        icon: <><path d="M12 8v4l3 3" /><circle cx="12" cy="12" r="9" /></>,
    },
    {
        filter: 'Running', cardClass: 'ah-kpi-running', iconClass: 'ah-icon-running', valueClass: 'ah-kpi-value-running',
        label: 'Running Now', subtext: 'currently executing',
        icon: null, // filled play glyph, rendered separately
    },
];

function AutomationHealth() {
    usePageTitle('Automation Health');

    const [query, setQuery]     = useState('');
    const [filter, setFilter]   = useState('All');
    const [selected, setSelected] = useState(null);

    const counts = useMemo(() => {
        const tally = {};
        AUTOMATION_HEALTH_ROWS.forEach(r => { tally[r.status] = (tally[r.status] || 0) + 1; });
        return tally;
    }, []);

    const visibleRows = useMemo(() => {
        const q = query.trim().toLowerCase();
        return AUTOMATION_HEALTH_ROWS.filter(row => {
            if (filter !== 'All' && row.status !== filter) return false;
            if (!q) return true;
            return row.name.toLowerCase().includes(q)
                || row.folder.toLowerCase().includes(q)
                || row.journey.toLowerCase().includes(q);
        });
    }, [query, filter]);

    const toggleKpi = value => setFilter(current => (current === value ? 'All' : value));

    return (
        <>
            <Menu />

            <div className="container fade-up container-main">
                <div className="page-header mb-4">
                    <h1>Automation Health</h1>
                    <p>Live run status across every automation — errors, stale schedules, and in-flight jobs</p>
                </div>

                <div className="row g-3 mb-4">
                    {KPIS.map(kpi => (
                        <div className="col-6 col-md-3" key={kpi.filter}>
                            <button
                                type="button"
                                className={`kpi-card ah-kpi-card ${kpi.cardClass} d-flex align-items-start gap-3${filter === kpi.filter ? ' active' : ''}`}
                                onClick={() => toggleKpi(kpi.filter)}
                            >
                                <div className={`kpi-icon ${kpi.iconClass}`}>
                                    {kpi.icon ? (
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                             stroke="currentColor" strokeWidth="2">
                                            {kpi.icon}
                                        </svg>
                                    ) : (
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                            <polygon points="6,4 20,12 6,20" />
                                        </svg>
                                    )}
                                </div>
                                <div>
                                    <p className="kpi-label">{kpi.label}</p>
                                    <p className={`kpi-value ${kpi.valueClass}`}>{counts[kpi.filter] || 0}</p>
                                    <p className="kpi-subtext">{kpi.subtext}</p>
                                </div>
                            </button>
                        </div>
                    ))}
                </div>

                <div className="d-flex flex-column flex-lg-row gap-3 mb-3 align-items-stretch align-items-lg-center">
                    <div className="search-wrapper">
                        <svg className="search-icon" width="15" height="15" viewBox="0 0 24 24"
                             fill="none" stroke="#94A3B8" strokeWidth="2">
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.35-4.35" />
                        </svg>
                        <input
                            type="text"
                            className="search-input"
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            placeholder="Search automations, folders or journeys…"
                        />
                    </div>
                    <div className="filter-buttons ah-filters d-flex flex-wrap gap-1">
                        {FILTERS.map(f => (
                            <button
                                key={f}
                                type="button"
                                className={`filter-btn${filter === f ? ' active' : ''}`}
                                onClick={() => setFilter(f)}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                </div>

                <p className="de-results-count mb-3">
                    Showing <strong>{visibleRows.length}</strong> of {AUTOMATION_HEALTH_ROWS.length} automations
                </p>

                <div className="d-flex flex-column gap-3">
                    {visibleRows.length === 0 ? (
                        <p className="xbu-empty">No automations match the current filters.</p>
                    ) : visibleRows.map(row => (
                        <button
                            type="button"
                            key={row.name}
                            className="email-row-card ah-row"
                            onClick={() => setSelected(row)}
                        >
                            <div className="email-accent-stripe"
                                 style={{ background: AUTOMATION_STATUS_COLORS[row.status] }} />
                            <div className="email-card-content">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                            <h3 className="email-card-title">{row.name}</h3>
                                            <span className={`ah-badge ${AUTOMATION_BADGE_CLASS[row.status]}`}>
                                                <span className="ah-badge-dot" />{row.status}
                                            </span>
                                        </div>
                                        <p className="email-meta-text mb-0">{row.folder} · {row.journey}</p>
                                    </div>
                                    <span className="ah-chevron" aria-hidden="true">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                             stroke="currentColor" strokeWidth="2.5">
                                            <path d="M9 6l6 6-6 6" />
                                        </svg>
                                    </span>
                                </div>
                                <div className="email-metrics-row">
                                    <div className="email-metric-item">
                                        <p className="email-metric-label">Last Run</p>
                                        <p className="email-metric-value" style={row.lastRunColor ? { color: row.lastRunColor } : undefined}>
                                            {row.lastRunLabel || row.lastRun}
                                        </p>
                                    </div>
                                    <div className="email-metric-item">
                                        <p className="email-metric-label">Duration</p>
                                        <p className="email-metric-value">{row.duration}</p>
                                    </div>
                                    <div className="email-metric-item">
                                        <p className="email-metric-label">Next Run</p>
                                        <p className="email-metric-value">{row.nextRun}</p>
                                    </div>
                                    <div className="email-metric-item">
                                        <p className="email-metric-label">Folder</p>
                                        <p className="email-metric-value">{row.folder}</p>
                                    </div>
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Detail panel */}
            <div className={`ah-panel-backdrop${selected ? ' show' : ''}`} onClick={() => setSelected(null)} />
            <div className={`ah-panel-slide${selected ? ' show' : ''}`} data-ah-status={selected ? selected.status : ''}>
                {selected && (
                    <>
                        <div className="ah-panel-hero">
                            <button type="button" className="ah-panel-close"
                                    onClick={() => setSelected(null)} aria-label="Close">✕</button>
                            <div className="d-flex align-items-center gap-3">
                                <div className="ah-panel-hero-icon">
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                                         stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="9" />
                                        <path d="M12 8v4l3 2" />
                                    </svg>
                                </div>
                                <div className="ah-panel-hero-copy">
                                    <span className={`ah-badge ${AUTOMATION_BADGE_CLASS[selected.status]} mb-2`}>
                                        <span className="ah-badge-dot" />{selected.status}
                                    </span>
                                    <h2 className="ah-panel-title">{selected.name}</h2>
                                    <p className="ah-panel-hero-sub">{selected.folder}</p>
                                </div>
                            </div>
                        </div>

                        <div className="ah-panel-body">
                            {selected.error && (
                                <div className="ah-error-box mb-3">
                                    <div className="d-flex align-items-start gap-2">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                             stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="10" />
                                            <path d="M12 8v5M12 16h.01" />
                                        </svg>
                                        <div>
                                            <strong>Last run failed</strong>
                                            <p className="mb-0 mt-1">{selected.error}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <p className="ah-panel-section">Run snapshot</p>
                            <div className="ah-panel-metrics mb-4">
                                <div className="ah-panel-metric">
                                    <div className="ah-panel-metric-icon" style={{ background: '#6366F114', color: '#4F46E5' }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="9" /><path d="M12 8v4l3 2" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="ah-panel-metric-label">Last run</p>
                                        <p className="ah-panel-metric-value">{selected.lastRun}</p>
                                    </div>
                                </div>
                                <div className="ah-panel-metric">
                                    <div className="ah-panel-metric-icon" style={{ background: '#0EA5E914', color: '#0284C7' }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M12 2v4M12 18v4M4.9 4.9l2.8 2.8M16.3 16.3l2.8 2.8M2 12h4M18 12h4" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="ah-panel-metric-label">Duration</p>
                                        <p className="ah-panel-metric-value">{selected.duration}</p>
                                    </div>
                                </div>
                                <div className="ah-panel-metric">
                                    <div className="ah-panel-metric-icon" style={{ background: '#10B98114', color: '#059669' }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="3" y="4" width="18" height="18" rx="2" />
                                            <path d="M16 2v4M8 2v4M3 10h18" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="ah-panel-metric-label">Next run</p>
                                        <p className="ah-panel-metric-value">{selected.nextRun}</p>
                                    </div>
                                </div>
                                <div className="ah-panel-metric">
                                    <div className="ah-panel-metric-icon" style={{ background: '#8B5CF614', color: '#7C3AED' }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="3" y="3" width="7" height="7" rx="1" />
                                            <rect x="14" y="14" width="7" height="7" rx="1" />
                                            <path d="M14 7h7M3 17h7" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="ah-panel-metric-label">Steps</p>
                                        <p className="ah-panel-metric-value">{selected.steps}</p>
                                    </div>
                                </div>
                            </div>

                            <p className="ah-panel-section">Details</p>
                            <div className="ah-panel-details mb-4">
                                <div className="ah-panel-detail-row">
                                    <span className="ah-panel-detail-label">Related journey</span>
                                    <span className="ah-panel-detail-value">{selected.journey}</span>
                                </div>
                                <div className="ah-panel-detail-row">
                                    <span className="ah-panel-detail-label">Folder path</span>
                                    <span className="ah-panel-detail-value">{selected.folder}</span>
                                </div>
                            </div>
                        </div>

                        <div className="ah-panel-footer">
                            <NavLink className="ah-open-btn" to="/automations">
                                Open in Automations
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                                     stroke="currentColor" strokeWidth="2.5">
                                    <path d="M9 6l6 6-6 6" />
                                </svg>
                            </NavLink>
                        </div>
                    </>
                )}
            </div>
        </>
    );
}

export default AutomationHealth;
